<template>
  <div :class="bem({ legacyWidth, narrowContent })">
    <template
      v-for="(row, rowIndex) in filteredRows"
      :key="'base-row-' + row.id"
    >
      <BaseRow
        v-show="!row.hidden"
        :id="`row-${row.id}`"
        ref="item"
        class="ContentLoop__row"
        :class="[
          bem('row', {
            pt: row.contentLoopSectionFormatting?.paddingTop || 'none',
            pb: row.contentLoopSectionFormatting?.paddingBottom || 'none',
            background: row.contentLoopSectionFormatting?.background,
            desktopOnly: row.visibility === 'desktop',
            mobileOnly: row.visibility === 'mobile',
            multiColumn: row.type === 'multi_column',
            centerBanner: row.type === 'banner' && row.fullWidth,
            sticky: row.sticky,
            aboveAnchors:
              hasAnchorLinks &&
              filteredRows?.[rowIndex + 1]?.type === 'anchor_button_row',
          }),
          { [`js-content-${row.type}`]: row.type },
          {
            'is-first':
              rowIndex === 0 || (rowIndex === 1 && row.visibility === 'mobile'),
          },
        ]"
        :data-anchor="anchorValues[rowIndex]"
      >
        <MediaHeroRow
          v-if="row.type === 'media_hero'"
          :block="row"
          :row-position="rowIndex"
        />

        <HeadingRow
          v-if="row.type === 'heading'"
          :key="row.id"
          :heading="{
            text: row.title,
            size: row.titleSize,
            tag: row.titleTag || 'h2',
            titleGradient: row.titleGradient,
          }"
          :subtitle="{
            text: row.subtitle,
            tag: row.subtitleTag || 'h3',
            fullWidth: row.fullWidthSubtitle,
            subtitleGradient: row.subtitleGradient,
          }"
          :copy="{
            text: row.copy,
            size: row.copySize,
            tag: row.copyTag || 'p',
            fullWidth: row.fullWidthCopy,
          }"
          :cta="{
            showCta: row.showCta,
            text: row.ctaCopy,
            link: row.ctaLink,
          }"
          :center-content="row.centerContent"
        />

        <Banner
          v-if="row.type === 'banner'"
          :key="row.id"
          :image="row.image"
          :overlay-opacity="row.overlayOpacity"
          :mobile-image="row.mobileImage"
          :mobile-overlay-opacity="row.mobileOverlayOpacity"
          :heading="{
            text: row.displayTitle,
            size: row.titleSize,
            tag: row.titleTag || 'h2',
          }"
          :subtitle="row.subtitle"
          aspect-ratio="16:9"
          :copy="row.copy"
          :use-rich-text="row.useRichText"
          :rich-text-copy="row.richTextCopy"
          :half-width-copy="row.halfWidthCopy"
          :full-width-copy="row.fullWidthCopy"
          :inline-copy="row.inlineCopy"
          :primary-colour="row.primaryColour"
          :heading-gradient="row.headingGradient"
          :expand-content="row.expandContent"
          :cta="{
            active: row.showCta,
            copy: row.ctaCopy,
            link: row.ctaLink,
            gradient: row.ctaGradient,
          }"
          :full-width="row.fullWidth"
          :banner-height-auto="row.bannerHeightAuto"
          :match-height-to-content="row.matchHeightToContent"
          :min-height="row.minHeight"
          :is-first="
            rowIndex === 0 ||
              (rowIndex === 1 &&
                row.visibility ===
                'mobile') /* disable parallax and fade on first banner */
          "
          :disable-parallax="row.disableParallax"
          :content-alignment="row.contentAlignment"
          :bottom-padding="row.bottomPadding"
          :features="row.features"
        />

        <StyledBanner
          v-if="row.type === 'styled_banner'"
          :key="row.id"
          :background-colour="row.backgroundColour"
          :condensed="row.condensedView"
          :banner-style="row.style"
          :image="row.image"
          :mobile-image="row.mobileImage"
          :content="row.content"
          :text-colour="row.textColour"
        />

        <StandFirst
          v-if="row.type === 'standfirst'"
          :key="row.id"
          :large-title="row.largeTitle"
          :title="row.title"
          :title-tag="row.titleTag"
          :subtitle="row.subtitle"
          :full-width="row.fullWidth"
        />

        <BaseDivider
          v-if="row.type === 'divider'"
          :key="row.id"
        />

        <div
          v-if="row.type === 'multi_column'"
          :class="[bem('multi-column'), 'global-wrapper-large']"
        >
          <BaseColumn
            v-for="(column, idx) in row.columns"
            :key="`${column.id}-${idx}`"
            :cols="row.columns.length"
            :class="bem('column', { blank: column.type === 'blank' })"
          >
            <ProductData
              v-if="column.type === 'product_card'"
              :key="column.id"
              :use-base-product="column.useBaseProduct"
              :base-product-sku="
                column.useBaseProduct
                  ? column.baseProduct
                    ? column.baseProduct.sku
                    : null
                  : null
              "
              :products="column.useBaseProduct ? [] : column.products"
              :variant-quantity-limit="5"
              :use-prescription-pricing="column.isPrescriptionProducts"
              :tag="{ text: column.tagCopy }"
              :show-gift-card="column.showGiftCard"
              :lite="true"
            >
              <template #default="{ products }">
                <LiteProductCard
                  :product="products"
                  @click-giftcard="$emit('click-giftcard', $event)"
                />
              </template>
            </ProductData>

            <StackRow
              v-if="column.type === 'stack'"
              :media="column.image"
              :row-has-media="rowHasMedia(row)"
              :aspect-ratio="column.aspectRatio"
              :heading="{
                text: column.title,
                size: column.titleSize,
                tag: column.titleTag,
              }"
              :subtitle="column.subtitle"
              :link="column.link"
              :copy="{
                text: column.copy,
                size: column.copySize,
              }"
              :size="row.columns.length > 2 ? 'small' : 'regular'"
              :cta="{
                active: column.showCta,
                copy: column.ctaCopy,
                link: column.ctaLink,
                type: column.ctaType,
                style: column.ctaStyle,
              }"
              :full-height="column.fullHeight"
            />

            <BannerCard
              v-if="column.type === 'banner_card'"
              :image="column.image"
              :primary-colour="column.primaryColour"
              :heading="{
                text: column.displayTitle,
                size: column.titleSize,
              }"
              aspect-ratio="16:9"
              :copy="column.copy"
            />

            <SustainabilityBlock
              v-if="column.type === 'content_block' && column.model.type === 'sustainability_block'"
              :block="column.model"
              :columns="row.columns.length"
            />
          </BaseColumn>
        </div>

        <BaseVideo
          v-if="row.type === 'video'"
          :key="row.id"
          :will-loop="row.loop"
          :is-muted="row.muted"
          :will-autoplay="false"
          :video="row.video"
          :full-width="row.fullWidth"
          :preview-start-time="row.previewStartTime || 0"
        />

        <RichText
          v-if="row.type === 'rich_text'"
          :key="row.id"
          :content="row.content"
          :match-padding-to-banner="row.matchPaddingToBanner"
          as-block
          :cta="{
            active: row.showCta,
            copy: row.ctaCopy,
            link: row.ctaLink,
            gradient: row.ctaGradient,
          }"
        />

        <LandingPageProductSection
          v-if="row.type === 'landing_page_product_section'"
          :key="row.id"
          v-bind="row"
        />

        <!-- I don't believe this is used anymore and appears to be a bit funky. -->
        <!-- <CompetitionProductSection
          v-if="row.type === 'competition_product_section'"
          :key="row.id"
          :row-index="rowIndex"
          v-bind="row"
        /> -->

        <Carousel
          v-if="row.type === 'carousel'"
          :block="row"
        />

        <CenteredCarousel
          v-if="row.type === 'centered_carousel'"
          :block="row"
          :row-position="rowIndex"
        />

        <SustainabilityBlock
          v-if="row.type === 'sustainability_block'"
          :key="row.id"
          :block="row"
        />

        <FormBlock
          v-if="row.type === 'form_block'"
          :form-id="row.formId"
        />

        <FAQBlock
          v-if="row.type === 'faq_block'"
          :key="row.id"
          :title="row.title"
          :articles="row.articles"
          :footer-copy="row.footerCopy"
        />

        <AnchorButtonRow
          v-if="row.type === 'anchor_button_row'"
          :key="row.id"
          :buttons="row.anchorButtonContainer"
          :sticky="row.sticky"
        />

        <EmbeddedCompetition
          v-if="row.type === 'competition_block'"
          :key="row.id"
          :competition="row.competition"
        />

        <DataCaptureBlock
          v-if="row.type === 'data_capture_block'"
          :key="row.id"
          :block="row"
        />

        <MediaHeader
          v-if="row.type === 'media_header'"
          :key="row.id"
          :block="row"
          :row-position="rowIndex"
        />

        <GridGallery
          v-if="row.type === 'gallery_grid'"
          :key="row.id"
          :block="row"
          :row-position="rowIndex"
        />

        <EmailSignup
          v-if="row.type === 'email_signup'"
          :key="row.id"
          :block="row"
          :row-position="rowIndex"
        />

        <MarketingRow
          v-if="row.type === 'marketing_row'"
          :key="row.id"
          :block="row"
          :row-position="rowIndex"
        />

        <InstagramShop
          v-if="row.type === 'instagram_shop'"
          :key="row.id"
          :block="row"
          :row-position="rowIndex"
        />

        <ImpactCarouselRow
          v-if="row.type === 'impact_carousel'"
          :key="row.id"
          :block="row"
          :row-position="rowIndex"
        />
        
        <CredibilityRow
          v-if="row.type === 'credibility_row'"
          :key="row.id"
          :block="row"
          :row-position="rowIndex"
        />

        <CardScroller
          v-if="row.type === 'card_scroller'"
          :block="row"
        />

        <StoreLocator
          v-if="row.type === 'store_locator_row'"
          :key="row.id"
          :block="row"
        />
      </BaseRow>
    </template>
  </div>
</template>

<script>
import { resolveContentLoopRows } from '~/assets/js/helpers/content-loop'

export default {
  name: 'ContentLoop',

  props: {
    rows: {
      type: Array,
      required: true,
    },
    activeVariantSku: {
      type: String,
      default: '',
    },
    nested: {
      type: Boolean,
      default: false,
    },
    narrowContent: {
      type: Boolean,
      default: false,
    },
    legacyWidth: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['click-giftcard'],

  setup () {
    const bem = useBEM('ContentLoop')
    return { bem }
  },

  data: () => ({}),

  computed: {
    resolvedRows() {
      return resolveContentLoopRows(this)
    },

    filteredRows() {
      return this.resolvedRows
        .filter(this.testSkuPattern)
        .filter(this.testURLParams)
        .filter(this.testSplitTests)
    },

    hasAnchorLinks() {
      return this.rows.some((row) => row.type === 'anchor_button_row')
    },

    anchorValues() {
      return this.filteredRows.map((row) => {
        if (!row.title) return null

        if (row?.title?.text) {
          return row?.title?.text?.replace(/\s+/g, '')
        }

        return row?.title?.replace(/\s+/g, '')
      })
    },
  },

  unmounted() {
    if (this.stackTl) {
      Object.keys(this.stackTl).forEach((item, index) => {
        this.stackTl[index].kill()
      })
    }

    if (this.rowTl) {
      Object.keys(this.rowTl).forEach((item, index) => {
        this.rowTl[index].kill()
      })
    }

    if (this.timeout) clearTimeout(this.timeout)
  },

  methods: {
    rowHasMedia(row) {
      return (
        row.columns.filter((column) => !!column.image && !!column.image.src)
          .length > 0
      )
    },

    getCurrentRowType(row) {
      return row.type
    },

    isLastRow(index) {
      return index + 1 === this.filteredRows.length
    },

    testSkuPattern(row) {
      if (!this.activeVariantSku) return true // If no active SKU is set then default to showing the block
      if (!row.skuPattern) return true // If no SKU pattern is set then show the block
      return !!this.activeVariantSku.match(row.skuPattern)
    },

    testURLParams(row) {
      if (!row.displayOnlyWithUrlParam) return true // If not filter is set then show the block
      const expectedParam = row.displayOnlyWithUrlParam
      return !!this.$route.query?.[expectedParam]
    },

    testSplitTests(row) {
      const splitTestVariant = row.splitTestVariant?.[0]
      if (!splitTestVariant) return true // If no split test variant is set then show the block

      const { splitTestKey, variantId } = splitTestVariant
      return this.$abt.variant(splitTestKey) === variantId
    },
  },
}
</script>

<style lang="postcss">
.ContentLoop {
  --screen-size: var(--large-screen-size);

  &--legacy-width {
    --screen-size: theme('screens.xxl');
  }
}

.is-default-standalone-theme {
  .ContentLoop__row:not(.ContentLoop__row--margin-none) {
    @apply mb-lg;
  }
}

.BaseRow.ContentLoop__row--desktop-only {
  @apply hidden md:flex;
}

.BaseRow.ContentLoop__row--mobile-only {
  @apply md:hidden;
}

.ContentLoop__multi-column {
  @apply flex md:space-x-sm mdMax:flex-col mdMax:space-y-sm;
}

.ContentLoop__row--center-banner {
  display: flex;
  justify-content: center;
}

.ContentLoop__row--pt-none {
  @apply pt-0;
}

.ContentLoop__row--pt-small {
  @apply pt-4 md:pt-5;
}

.ContentLoop__row--pt-medium {
  @apply pt-8 sm:pt-10;
}

.ContentLoop__row--pt-large {
  @apply pt-16 sm:pt-20;
}

.ContentLoop__row--pb-none {
  @apply pb-0;
}

.ContentLoop__row--pb-small {
  @apply pb-4 md:pb-5;
}

.ContentLoop__row--pb-medium {
  @apply pb-8 sm:pb-10;
}

.ContentLoop__row--pb-large {
  @apply pb-16 sm:pb-20;
}

.ContentLoop__row--background-light {
  @apply bg-white;
}

.ContentLoop__row--background-grey {
  @apply bg-grey-300;
}

.ContentLoop__row--background-dark {
  @apply bg-off-black;
}

.ContentLoop__row--background-light-to-grey {
  @apply bg-gradient-to-b from-white to-grey-300;
}

.ContentLoop__row--background-grey-to-light {
  @apply bg-gradient-to-b from-grey-300 to-white;
}

.ContentLoop__column {
  &--blank {
    @apply mdMax:hidden;
  }
}

.ContentLoop {
  &--narrow-content {
    --large-screen-size: theme('screens.xl');
  }
}

.ContentLoop__row--sticky {
  @apply sticky top-0 z-10  mb-10 sm:mb-20 bg-white;
}

.ContentLoop__row:has(+ .ContentLoop__row--sticky) {
  @apply mb-3 sm:mb-5;
}
</style>
